<template>
  <div>
    <b-form-group label="Title">
      <b-input v-model="content.title" placeholder="e.g Anbefalte artikler" />
    </b-form-group>

    <div class="mb-2">Links</div>

    <b-row v-for="(link, index) in content.links" :key="index" class="mb-3">
      <b-col cols="3">
        <b-form-select
          v-model="link.type"
          :options="linkTypes"
          @change="changeLinkType(index)"
          style="height: 40px;"
        />
      </b-col>

      <b-col>
        <multi-select
          v-model="selectedLinks[index]"
          placeholder="Choose an article"
          track-by="id"
          label="title"
          :options="optionsFor(link.type, index)"
          :multiple="false"
          :internal-search="false"
          :show-no-results="true"
          @open="findOptions(link.type)"
          @search-change="query => findOptions(link.type, query)"
          @select="article => selectLink(index, article)"
          @remove="removeSelection(index)"
        />
      </b-col>

      <b-col cols="auto">
        <b-button variant="danger" @click="removeLink(index)">
          <strong>&times;</strong>
        </b-button>
      </b-col>
    </b-row>

    <b-button variant="light" size="sm" @click="addNewLink">
      Add Link
    </b-button>
  </div>
</template>

<script>
import 'vue-multiselect/dist/vue-multiselect.min.css'
import MultiSelect from 'vue-multiselect'

export default {
  name: 'PagesRecommendedWidget',

  components: { MultiSelect },

  props: {
    value: { type: Object },
    resolved: { type: Array, required: false, default: () => [] },
    currentArticleId: { type: [Number, String], required: false, default: null },
  },

  data() {
    return {
      content: this.value,
      linkTypes: [{ value: 'article', text: 'Article' }],
      options: { article: [] },
      selectedLinks: this.value.links.map(link => {
        const resolvedLink = (this.resolved ?? []).find(item => item.id === link.id)

        if (resolvedLink) {
          link.title = resolvedLink.title
          return { id: resolvedLink.id, title: resolvedLink.title }
        }

        return link.id !== null && link.title ? { id: link.id, title: link.title } : null
      }),
    }
  },

  async mounted() {
    await this.findOptions('article')
  },

  methods: {
    addNewLink() {
      this.content.links.push({ type: 'article', id: null })
      this.selectedLinks.push(null)
    },

    removeLink(index) {
      this.content.links.splice(index, 1)
      this.selectedLinks.splice(index, 1)
    },

    changeLinkType(index) {
      this.$set(this.content.links[index], 'id', null)
      this.$delete(this.content.links[index], 'title')
      this.$set(this.selectedLinks, index, null)
      this.findOptions(this.content.links[index].type)
    },

    selectLink(index, selected) {
      this.$set(this.content.links[index], 'id', selected.id)
      this.$set(this.content.links[index], 'title', selected.title)
    },

    removeSelection(index) {
      this.$set(this.content.links[index], 'id', null)
      this.$delete(this.content.links[index], 'title')
    },

    optionsFor(type, currentIndex) {
      const selectedIds = new Set(
        this.content.links
          .filter((link, index) => index !== currentIndex && link.type === type && link.id !== null)
          .map(link => link.id)
      )
      if (type === 'article' && this.currentArticleId !== null) {
        selectedIds.add(this.currentArticleId)
      }

      return (this.options[type] ?? []).filter(option => !selectedIds.has(option.id))
    },

    async findOptions(type, query = '') {
      if (type !== 'article') return

      const response = await this.$store.dispatch('inspirations/listing', {
        page: 1,
        query: {
          title: query,
          listAll: true,
          withoutContent: true,
        },
      })

      this.$set(this.options, type, response.data())
      this.resolveSelectedLinks(type)
      this.addSelectedLinksToOptions(type)
    },

    resolveSelectedLinks(type) {
      this.content.links.forEach((link, index) => {
        if (link.type !== type || this.selectedLinks[index]) return

        const selected = (this.options[type] ?? []).find(option => option.id === link.id)
        if (selected) {
          this.$set(this.selectedLinks, index, selected)
          this.$set(link, 'title', selected.title)
        }
      })
    },

    addSelectedLinksToOptions(type) {
      const options = [...(this.options[type] ?? [])]

      this.selectedLinks.forEach((selected, index) => {
        if (
          selected &&
          this.content.links[index].type === type &&
          !options.some(option => option.id === selected.id)
        ) {
          options.push(selected)
        }
      })

      this.$set(this.options, type, options)
    },
  },
}
</script>
